import { useCallback, useEffect, useState } from 'react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { forgotPassword, resetPassword, RecoveryApiError } from './api';
import { parseRecoveryHash, type RecoveryView } from './format';
import { isValidPassword } from '../utils/passwordPolicy';

// Fase 27A-F: shell mínimo de recuperación de contraseña (pre-auth, bajo #forgot-password /
// #reset-password). Usa el lenguaje visual del login. Respuesta neutral (no revela existencia de cuenta).

const goLogin = () => { window.location.hash = ''; };

export function RecoveryApp() {
  const [route, setRoute] = useState<RecoveryView>(parseRecoveryHash(window.location.hash));
  useEffect(() => {
    const onHash = () => setRoute(parseRecoveryHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return (
    <div className="platform-login">
      {route.view === 'forgot' ? <ForgotView /> : <ResetView token={route.token} />}
    </div>
  );
}

function Brand({ subtitle }: { subtitle: string }) {
  return (
    <div className="platform-login__brand">
      <span className="platform-login__name">Negocio360</span>
      <span className="platform-login__tag">{subtitle}</span>
    </div>
  );
}

function ForgotView() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const canSubmit = /.+@.+\..+/.test(email);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !canSubmit) return;
    setBusy(true); setError(undefined);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      // Neutral incluso ante rate-limit: mostramos el mismo estado enviado salvo 429 explícito.
      if (err instanceof RecoveryApiError && err.status === 429) setError(err.message);
      else setSent(true);
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <form className="platform-login__card">
        <Brand subtitle="Recuperar contraseña" />
        <StatusBadge variant="info">Revisa tu correo</StatusBadge>
        <p className="platform-muted">Si existe una cuenta asociada a ese correo, recibirás instrucciones para restablecer tu contraseña.</p>
        <button className="button button--secondary" type="button" onClick={goLogin}>Volver a iniciar sesión</button>
      </form>
    );
  }

  return (
    <form className="platform-login__card" onSubmit={submit}>
      <Brand subtitle="Recuperar contraseña" />
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <p className="platform-muted">Ingresa tu correo y te enviaremos instrucciones para restablecer tu contraseña.</p>
      <label className="field"><span>Correo</span>
        <input type="email" autoComplete="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </label>
      <button className="button button--primary" disabled={busy || !canSubmit} type="submit">{busy ? 'Enviando…' : 'Enviar instrucciones'}</button>
      <button className="text-button" type="button" onClick={goLogin}>Volver a iniciar sesión</button>
    </form>
  );
}

function ResetView({ token }: { token: string | null }) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [state, setState] = useState<'form' | 'done' | 'invalid'>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  // Limpia el token de la URL visible (anti-Referer/historial) una vez leído en memoria.
  useEffect(() => {
    if (token) { try { history.replaceState(null, '', `${window.location.pathname}${window.location.search}#reset-password`); } catch { /* noop */ } }
  }, [token]);

  // ONB-002: misma política que el servidor: PIN de 4 a 8 números o contraseña de cuenta (8+ con letras y números).
  const policyOk = isValidPassword(pw1);
  const match = pw1 === pw2;
  const canSubmit = !!token && policyOk && match;

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !canSubmit || !token) return;
    setBusy(true); setError(undefined);
    try {
      await resetPassword(token, pw1);
      setPw1(''); setPw2('');
      setState('done');
    } catch (err) {
      if (err instanceof RecoveryApiError && err.code === 'RESET_INVALID_OR_EXPIRED_TOKEN') setState('invalid');
      else setError(err instanceof Error ? err.message : 'No fue posible restablecer la contraseña.');
    } finally { setBusy(false); }
  }, [busy, canSubmit, token, pw1]);

  if (!token) {
    return <div className="platform-login__card"><Brand subtitle="Restablecer contraseña" /><StatusBadge variant="danger">Enlace inválido</StatusBadge><p className="platform-muted">Falta el token del enlace. Solicita uno nuevo.</p><a className="button button--primary" href="#forgot-password" onClick={() => { window.location.hash = 'forgot-password'; }}>Solicitar nuevo enlace</a></div>;
  }
  if (state === 'done') {
    return <div className="platform-login__card"><Brand subtitle="Restablecer contraseña" /><StatusBadge variant="success">Contraseña actualizada</StatusBadge><p className="platform-muted">Tu contraseña se actualizó. Ya puedes iniciar sesión.</p><button className="button button--primary" type="button" onClick={goLogin}>Iniciar sesión</button></div>;
  }
  if (state === 'invalid') {
    return <div className="platform-login__card"><Brand subtitle="Restablecer contraseña" /><StatusBadge variant="danger">Enlace inválido o expirado</StatusBadge><p className="platform-muted">Este enlace ya se usó o venció. Solicita uno nuevo.</p><a className="button button--primary" href="#forgot-password" onClick={() => { window.location.hash = 'forgot-password'; }}>Solicitar nuevo enlace</a></div>;
  }

  return (
    <form className="platform-login__card" onSubmit={submit}>
      <Brand subtitle="Restablecer contraseña" />
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <label className="field"><span>Nueva contraseña</span>
        <input type="password" autoComplete="new-password" maxLength={72} value={pw1} onChange={(e) => setPw1(e.target.value)} autoFocus />
      </label>
      <p className="platform-muted">Al menos 8 caracteres con letras y números, o un PIN de 4 a 8 números.</p>
      <label className="field"><span>Confirmar contraseña</span>
        <input type="password" autoComplete="new-password" maxLength={72} value={pw2} onChange={(e) => setPw2(e.target.value)} />
      </label>
      {pw2.length > 0 && !match && <p className="platform-muted" role="alert">Las contraseñas no coinciden.</p>}
      <button className="button button--primary" disabled={busy || !canSubmit} type="submit">{busy ? 'Guardando…' : 'Restablecer contraseña'}</button>
      <button className="text-button" type="button" onClick={goLogin}>Volver a iniciar sesión</button>
    </form>
  );
}
